import { ShieldCheck, Smartphone, Zap, ArrowRight, Wrench } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { track } from "@/lib/track";
import { Card } from "@/components/ui/card";
import { TOOLS } from "@/lib/tools";

const GENERAL_FAQ = [
  { q: "Was ist die Rechnerkiste?", a: "Eine Sammlung kleiner, praktischer Rechner und Werkzeuge für Alltag, Heimwerken, Umzug und Haushalt. Jedes Tool löst eine konkrete Frage, kostenlos und ohne Anmeldung." },
  { q: "Kosten die Tools etwas?", a: "Nein. Alle Tools sind kostenlos und ohne Anmeldung nutzbar." },
  { q: "Brauche ich ein Konto?", a: "Nein. Du kannst alle Rechner direkt benutzen." },
  { q: "Werden meine Eingaben gespeichert?", a: "Die meisten Rechner laufen vollständig im Browser. Was im Einzelnen passiert, steht in der Datenschutzerklärung des jeweiligen Tools." },
];

export default function HomePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cats = [...new Set(TOOLS.map((t) => t.cat))];
  const grouped = cats.map((c) => ({ cat: c, items: TOOLS.filter((t) => t.cat === c) }));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Rechnerkiste",
      url: origin || undefined,
      inLanguage: "de-DE",
      description: "Sammlung praktischer Rechner und Werkzeuge für Alltag, Heimwerken, Umzug und Haushalt.",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: TOOLS.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: t.name,
        url: t.url,
        description: t.tagline,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GENERAL_FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div>
      <Seo
        title="Rechnerkiste: kostenlose Rechner für Alltag, Heimwerken, Küche und Finanzen"
        description="Über 15 kostenlose Rechner an einem Ort: Prozent, Mehrwertsteuer, Beton, Teppichgröße, Portionen, Spritkosten und mehr. Ohne Anmeldung, sofort nutzbar."
      canonical={origin ? `${origin}/` : undefined}
      />
      <JsonLd data={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.10), rgba(249,115,22,0.08), rgba(251,250,247,0))", height: "60%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-6 relative">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Die Rechnerkiste
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Über {TOOLS.length} kleine, praktische Rechner für Alltag, Heimwerken, Küche, Umzug und Finanzen. Jeder löst eine konkrete Frage, schnell, kostenlos und ohne Anmeldung.
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Ohne Anmeldung</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Mobil optimiert</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> Sofort nutzbar</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tool-Grid */}
      <section id="tools" className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-xl sm:text-2xl font-semibold">Alle Tools</h2>
        <div className="mt-6 space-y-8">
          {grouped.map(({ cat, items }) => (
            <div key={cat}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">{cat}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((t) => (
                  <a key={t.id} href={t.url} data-testid={`tool-${t.id}`} className="block" onClick={() => track("crosslink_click", { target: t.id })}>
                    <Card className="p-4 h-full hover:shadow-md transition-shadow border flex flex-col gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                          <Wrench className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-semibold">{t.name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t.tagline}</p>
                      <span className="mt-auto inline-flex items-center gap-1 text-sm text-primary font-medium">
                        Zum Tool <ArrowRight className="h-4 w-4" />
                      </span>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Anzeige */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6"><AdSlot variant="banner" /></div>

      {/* Was ist die Rechnerkiste */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="text-xl sm:text-2xl font-semibold mb-3">Was ist die Rechnerkiste?</h2>
        <p className="text-muted-foreground leading-relaxed">
          Die Rechnerkiste ist eine wachsende Sammlung kleiner Werkzeuge, die jeweils eine konkrete Frage beantworten. Statt einer großen App findest du hier einzelne, fokussierte Tools. Wähle oben das passende und leg direkt los.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fragen</h2>
        <Faq items={GENERAL_FAQ} testId="home-faq-accordion" />
      </section>

      {/* Anzeige */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12"><AdSlot variant="banner" /></div>
    </div>
  );
}
